import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getSpecialties } from "@/services/sintegrada/specialty/getSpecialties";
import { getDepartments } from "@/services/sintegrada/departments/getDepartments";
import { Option } from "@/@types/generics/Option";
import { Specialty } from "@/@types/sintegrada/Specialty";
import { Department } from "@/@types/sintegrada/Department";
import { InternalDepartmentTypeEnum } from "@/utils/enums/sintegrada/occupation/InternalDepartmentTypeEnum";

import { SelectComponent, SelectComponentProps } from "@/components/SelectComponent";

type Props = SelectComponentProps & {
	showInternalDepartments?: boolean;
	handleChangeClinicOrSpecialty: (option: Option<ClinicOrSpecialtyOptionValue>) => void;
};

export type ClinicOrSpecialtyOptionValue = {
	department: Department;
	specialty: Specialty;
};

export function ClinicOrSpecialtySelect({
	showInternalDepartments,
	handleChangeClinicOrSpecialty,
	...rest
}: Props) {
	const { uf, ibgeCode, cnes } = useCurrentAccount();

	async function loadOptions(search: string): Promise<Option<ClinicOrSpecialtyOptionValue>[]> {
		const departments = await getDepartments({
			uf,
			ibgeCode,
			cnes,
		});

		const clinic =
			departments.find((department) => department.name === "Consultórios") ||
			({} as Department);

		const internalDepartmentNames = [
			InternalDepartmentTypeEnum.Internment,
			InternalDepartmentTypeEnum.RedRoom,
			InternalDepartmentTypeEnum.Observation,
		];

		const internalDepartments = departments.filter((department) =>
			internalDepartmentNames.includes(department.name as InternalDepartmentTypeEnum)
		);

		const specialtyClinic =
			departments.find((department) => department.name === "Consultório Especialidade") ||
			({} as Department);

		const specialties = await getSpecialties({
			uf,
			ibgeCode,
			cnes,
		});

		const defaultOption = {
			label: clinic.name,
			value: {
				department: clinic,
				specialty: {} as Specialty,
			},
		};

		const internalDepartmentOptions = showInternalDepartments
			? internalDepartments.map((internalDepartment) => ({
					label: internalDepartment.name,
					value: {
						department: internalDepartment,
						specialty: {} as Specialty,
					},
			  }))
			: [];

		const specialtyOptions = specialties.map((specialty) => ({
			label: `Especialidade - ${specialty.name}`,
			value: {
				department: specialtyClinic,
				specialty,
			},
		}));

		handleChangeClinicOrSpecialty(defaultOption);

		const options = [defaultOption, ...internalDepartmentOptions, ...specialtyOptions];

		return options.filter((option) =>
			option.label.toLowerCase().includes(search.toLowerCase())
		);
	}

	return (
		<SelectComponent
			isAsync
			defaultOptions
			placeholder="Selecione..."
			loadOptions={loadOptions}
			onChange={(option) =>
				handleChangeClinicOrSpecialty(option as Option<ClinicOrSpecialtyOptionValue>)
			}
			{...rest}
		/>
	);
}
